import React, { useState, useMemo, useRef } from 'react';
import { useThemeTokens } from '../../../utils/themeTokens';

const NODE_HEIGHT = 32;
const NODE_GAP = 8;
const NODE_WIDTH = 140;
const SVG_PADDING_X = 16;
const SVG_PADDING_Y = 16;
const LINK_AREA = 100;

const truncate = (str, max = 18) => (str.length > max ? str.slice(0, max) + '...' : str);

const RouteNetworkDiagram = ({ data }) => {
  const t = useThemeTokens();
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);

  const { sources, targets, links, maxCount, svgHeight, nodeStats } = useMemo(() => {
    if (!data || data.length === 0)
      return { sources: [], targets: [], links: [], maxCount: 0, svgHeight: 100, nodeStats: {} };

    const sourceSet = new Set();
    const targetSet = new Set();
    const stats = {};
    let max = 0;

    data.forEach((d) => {
      sourceSet.add(d.source);
      targetSet.add(d.target);
      if (d.tripCount > max) max = d.tripCount;

      const sKey = `s-${d.source}`;
      const tKey = `t-${d.target}`;
      if (!stats[sKey]) stats[sKey] = { trips: 0, tonnage: 0, routes: 0 };
      if (!stats[tKey]) stats[tKey] = { trips: 0, tonnage: 0, routes: 0 };
      stats[sKey].trips += d.tripCount;
      stats[sKey].tonnage += d.totalTonnage;
      stats[sKey].routes += 1;
      stats[tKey].trips += d.tripCount;
      stats[tKey].tonnage += d.totalTonnage;
      stats[tKey].routes += 1;
    });

    const sourceList = [...sourceSet].sort();
    const targetList = [...targetSet].sort();
    const maxNodes = Math.max(sourceList.length, targetList.length);
    const height = SVG_PADDING_Y * 2 + maxNodes * (NODE_HEIGHT + NODE_GAP) - NODE_GAP;

    return {
      sources: sourceList,
      targets: targetList,
      links: data,
      maxCount: max,
      svgHeight: Math.max(height, 120),
      nodeStats: stats,
    };
  }, [data]);

  const totalTrips = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, d) => sum + d.tripCount, 0);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-6 text-center">
        <p className="text-sm text-slate-400">No route data available</p>
      </div>
    );
  }

  const svgWidth = SVG_PADDING_X * 2 + NODE_WIDTH * 2 + LINK_AREA;

  const getSourceY = (name) => {
    const idx = sources.indexOf(name);
    const totalHeight = sources.length * (NODE_HEIGHT + NODE_GAP) - NODE_GAP;
    const startY = (svgHeight - totalHeight) / 2;
    return startY + idx * (NODE_HEIGHT + NODE_GAP) + NODE_HEIGHT / 2;
  };

  const getTargetY = (name) => {
    const idx = targets.indexOf(name);
    const totalHeight = targets.length * (NODE_HEIGHT + NODE_GAP) - NODE_GAP;
    const startY = (svgHeight - totalHeight) / 2;
    return startY + idx * (NODE_HEIGHT + NODE_GAP) + NODE_HEIGHT / 2;
  };

  const sourceX = SVG_PADDING_X + NODE_WIDTH;
  const targetX = SVG_PADDING_X + NODE_WIDTH + LINK_AREA;

  const showLinkTooltip = (e, link) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = totalTrips > 0 ? ((link.tripCount / totalTrips) * 100).toFixed(1) : 0;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      type: 'link',
      source: link.source,
      target: link.target,
      tripCount: link.tripCount,
      totalTonnage: link.totalTonnage,
      avgWeight: link.avgWeight,
      activeDays: link.activeDays,
      percentage: pct,
    });
  };

  const showNodeTooltip = (e, nodeKey, name, side) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const st = nodeStats[nodeKey] || { trips: 0, tonnage: 0, routes: 0 };
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      type: 'node',
      name,
      side,
      trips: st.trips,
      tonnage: st.tonnage,
      routes: st.routes,
    });
  };

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100">
        <h3 className="text-base font-semibold text-slate-800">Route Network</h3>
        <p className="text-xs text-slate-400 mt-0.5">
          {links.length} route{links.length !== 1 ? 's' : ''} between {sources.length} dispatch and{' '}
          {targets.length} receipt locations
        </p>
      </div>

      <div ref={containerRef} className="px-3 py-3 relative">
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full"
            style={{ minHeight: Math.min(svgHeight, 400), maxHeight: 500 }}
          >
            {/* Links */}
            {links.map((link, i) => {
              const y1 = getSourceY(link.source);
              const y2 = getTargetY(link.target);
              const x1 = sourceX;
              const x2 = targetX;
              const cx = (x1 + x2) / 2;
              const strokeWidth = Math.max(1.5, (link.tripCount / maxCount) * 14);
              const isHighlighted =
                hoveredLink === i ||
                hoveredNode === `s-${link.source}` ||
                hoveredNode === `t-${link.target}`;
              const isOther = (hoveredLink !== null || hoveredNode !== null) && !isHighlighted;

              return (
                <path
                  key={i}
                  d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke="#45B7AA"
                  strokeWidth={strokeWidth}
                  opacity={isOther ? 0.08 : isHighlighted ? 0.85 : 0.3}
                  strokeLinecap="round"
                  className="transition-opacity duration-150 cursor-pointer"
                  onMouseEnter={(e) => {
                    setHoveredLink(i);
                    showLinkTooltip(e, link);
                  }}
                  onMouseMove={(e) => showLinkTooltip(e, link)}
                  onMouseLeave={() => {
                    setHoveredLink(null);
                    setTooltip(null);
                  }}
                />
              );
            })}

            {/* Source nodes (left) */}
            {sources.map((name) => {
              const y = getSourceY(name) - NODE_HEIGHT / 2;
              const isHovered = hoveredNode === `s-${name}`;
              return (
                <g
                  key={`s-${name}`}
                  onMouseEnter={(e) => {
                    setHoveredNode(`s-${name}`);
                    showNodeTooltip(e, `s-${name}`, name, 'dispatch');
                  }}
                  onMouseMove={(e) => showNodeTooltip(e, `s-${name}`, name, 'dispatch')}
                  onMouseLeave={() => {
                    setHoveredNode(null);
                    setTooltip(null);
                  }}
                  className="cursor-pointer"
                >
                  <rect
                    x={SVG_PADDING_X}
                    y={y}
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={6}
                    fill={isHovered ? 'rgba(69,183,170,0.15)' : t.surface}
                    stroke={isHovered ? '#45B7AA' : 'rgba(69,183,170,0.25)'}
                    strokeWidth={isHovered ? 1.5 : 1}
                  />
                  <text
                    x={SVG_PADDING_X + NODE_WIDTH / 2}
                    y={y + NODE_HEIGHT / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="text-[10px] font-medium"
                    fill={isHovered ? t.accentText : t.text}
                  >
                    {truncate(name)}
                  </text>
                </g>
              );
            })}

            {/* Target nodes (right) */}
            {targets.map((name) => {
              const y = getTargetY(name) - NODE_HEIGHT / 2;
              const isHovered = hoveredNode === `t-${name}`;
              return (
                <g
                  key={`t-${name}`}
                  onMouseEnter={(e) => {
                    setHoveredNode(`t-${name}`);
                    showNodeTooltip(e, `t-${name}`, name, 'receipt');
                  }}
                  onMouseMove={(e) => showNodeTooltip(e, `t-${name}`, name, 'receipt')}
                  onMouseLeave={() => {
                    setHoveredNode(null);
                    setTooltip(null);
                  }}
                  className="cursor-pointer"
                >
                  <rect
                    x={targetX}
                    y={y}
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={6}
                    fill={isHovered ? 'rgba(16,185,129,0.10)' : t.surface}
                    stroke={isHovered ? '#10b981' : 'rgba(16,185,129,0.2)'}
                    strokeWidth={isHovered ? 1.5 : 1}
                  />
                  <text
                    x={targetX + NODE_WIDTH / 2}
                    y={y + NODE_HEIGHT / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="text-[10px] font-medium"
                    fill={isHovered ? t.success : t.text}
                  >
                    {truncate(name)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* HTML Tooltip Overlay */}
        {tooltip && (
          <div
            className="absolute pointer-events-none z-50"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translate(-50%, -100%) translateY(-12px)',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16)',
                border: '1px solid rgba(255,255,255,0.08)',
                minWidth: 180,
                maxWidth: 260,
                animation: 'tooltipFadeIn 0.15s ease-out',
              }}
            >
              {tooltip.type === 'link' ? (
                <>
                  {/* Route label */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span
                      style={{
                        fontSize: 10,
                        color: '#94a3b8',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Route
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#5eead4', fontWeight: 600 }}>
                      {tooltip.source}
                    </span>
                    <svg width="16" height="8" viewBox="0 0 16 8" style={{ flexShrink: 0 }}>
                      <path
                        d="M0 4 L12 4 M9 1 L12 4 L9 7"
                        stroke="#475569"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span style={{ fontSize: 12, color: '#6ee7b7', fontWeight: 600 }}>
                      {tooltip.target}
                    </span>
                  </div>

                  {/* Trip count - hero metric */}
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(69,183,170,0.15) 0%, rgba(16,185,129,0.10) 100%)',
                      borderRadius: 8,
                      padding: '8px 10px',
                      marginBottom: 8,
                      border: '1px solid rgba(69,183,170,0.2)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                        <span
                          style={{ fontSize: 22, fontWeight: 700, color: '#5eead4', lineHeight: 1 }}
                        >
                          {tooltip.tripCount}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                          {tooltip.tripCount === 1 ? 'trip' : 'trips'}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          color: '#94a3b8',
                          fontWeight: 500,
                          background: 'rgba(255,255,255,0.06)',
                          padding: '2px 6px',
                          borderRadius: 4,
                        }}
                      >
                        {tooltip.percentage}%
                      </span>
                    </div>

                    {/* Mini progress bar */}
                    <div
                      style={{
                        marginTop: 6,
                        height: 3,
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(parseFloat(tooltip.percentage), 100)}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #45B7AA, #10b981)',
                          borderRadius: 2,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </div>

                  {/* Detail metrics */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>Tonnage</div>
                      <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>
                        {tooltip.totalTonnage.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}> t</span>
                      </div>
                    </div>
                    {tooltip.avgWeight > 0 && (
                      <div>
                        <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>
                          Avg weight
                        </div>
                        <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>
                          {tooltip.avgWeight.toLocaleString(undefined, {
                            maximumFractionDigits: 1,
                          })}
                          <span style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}>
                            {' '}
                            t
                          </span>
                        </div>
                      </div>
                    )}
                    {tooltip.activeDays > 0 && (
                      <div>
                        <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>
                          Active days
                        </div>
                        <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>
                          {tooltip.activeDays}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Node tooltip */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: tooltip.side === 'dispatch' ? '#45B7AA' : '#10b981',
                      }}
                    />
                    <span
                      style={{
                        fontSize: 10,
                        color: '#94a3b8',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {tooltip.side} location
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 600, marginBottom: 8 }}>
                    {tooltip.name}
                  </div>

                  <div style={{ display: 'flex', gap: 14 }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>Trips</div>
                      <div style={{ fontSize: 16, color: '#5eead4', fontWeight: 700 }}>
                        {tooltip.trips}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>Tonnage</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>
                        {tooltip.tonnage.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 400 }}> t</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 1 }}>Routes</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>
                        {tooltip.routes}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Tooltip caret */}
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid #1e293b',
                margin: '0 auto',
              }}
            />
          </div>
        )}

        {/* Tooltip animation keyframes */}
        <style>{`
          @keyframes tooltipFadeIn {
            from { opacity: 0; transform: translateY(4px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default RouteNetworkDiagram;
